/** Probate 176 — Stream F */
import DocumentUploadSection from '../../settlement/DocumentUploadSection';
import {
  probateFieldLabelClass,
  probateInputClass,
  ProbateBackLink,
  ProbateChecklistCard,
  ProbateChecklistProgress,
  ProbateContextBlock,
} from './probateStepUi';
import { useProbateDocuments } from './useProbateDocuments';
import { useProbateStep } from './useProbateStep';

const STEP_ID = 'prob-6';
const FORM_FIELDS = 'einNumber,bankName,accountLastFour,accountOpenedDate';

export default function SFrame176() {
  const idsEIN = ['prob-6-item-1', 'prob-6-item-2'];
  const idsBank = ['prob-6-item-3', 'prob-6-item-4', 'prob-6-item-5', 'prob-6-item-6'];

  const {
    estateId,
    isArchived,
    error,
    stepDef,
    g,
    checklistDefs,
    checklistCompleted,
    checklistTotal,
    checkedIds,
    toggleItem,
    formValues,
    setField,
    touchActivity,
  } = useProbateStep(STEP_ID, FORM_FIELDS);

  const pick = (ids: string[]) => checklistDefs.filter((c) => ids.includes(c.id));

  const ro = !!isArchived;
  const { documents, upload, remove } = useProbateDocuments(estateId, STEP_ID, ro);

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <ProbateBackLink estateId={estateId} />
      {error ? (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      ) : null}
      <h1 className="text-2xl font-semibold text-stone-800 mb-2">{stepDef?.title}</h1>
      {g?.point && g?.goal ? <ProbateContextBlock point={g.point} goal={g.goal} /> : null}
      <ProbateChecklistProgress completed={checklistCompleted} total={checklistTotal} />

      {/* Block 1 */}
      <h2 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">EIN setup</h2>
      <div className="bg-white rounded-xl border border-stone-200 p-5 mb-3 space-y-4">
        <h3 className="text-sm font-semibold text-stone-800">Inputs</h3>
        <div>
          <label className={probateFieldLabelClass} htmlFor="einNumber">EIN number</label>
          <input id="einNumber" className={probateInputClass} disabled={ro} placeholder="XX-XXXXXXX"
            value={formValues.einNumber ?? ''} onChange={(e) => setField('einNumber', e.target.value)} />
        </div>
      </div>
      <h4 className="text-sm font-semibold text-stone-800 uppercase tracking-wide mb-2">Checklist</h4>
      <ProbateChecklistCard items={pick(idsEIN)} checkedIds={checkedIds} toggleItem={toggleItem} disabled={ro} />

      {/* Block 2 */}
      <h2 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mt-6 mb-2">Bank account setup</h2>
      <div className="bg-white rounded-xl border border-stone-200 p-5 mb-3 space-y-4">
        <h3 className="text-sm font-semibold text-stone-800">Inputs</h3>
        <div>
          <label className={probateFieldLabelClass} htmlFor="bankName">Bank name</label>
          <input id="bankName" className={probateInputClass} disabled={ro}
            value={formValues.bankName ?? ''} onChange={(e) => setField('bankName', e.target.value)} />
        </div>
        <div>
          <label className={probateFieldLabelClass} htmlFor="accountLastFour">Account number (last 4 digits)</label>
          <input id="accountLastFour" inputMode="numeric" maxLength={4} className={probateInputClass} disabled={ro}
            value={formValues.accountLastFour ?? ''} onChange={(e) => setField('accountLastFour', e.target.value)} />
        </div>
        <div>
          <label className={probateFieldLabelClass} htmlFor="accountOpenedDate">Date opened</label>
          <input id="accountOpenedDate" type="date" className={probateInputClass} disabled={ro}
            value={formValues.accountOpenedDate ?? ''} onChange={(e) => setField('accountOpenedDate', e.target.value)} />
        </div>
      </div>
      <h4 className="text-sm font-semibold text-stone-800 uppercase tracking-wide mb-2">Checklist</h4>
      <ProbateChecklistCard items={pick(idsBank)} checkedIds={checkedIds} toggleItem={toggleItem} disabled={ro} />

      <div className="bg-white rounded-xl border border-stone-200 p-5 mt-4">
        <DocumentUploadSection
          documents={documents}
          onUpload={async (f) => { await touchActivity(); await upload(f); }}
          onDelete={ro ? undefined : (id) => remove(id)}
          disabled={ro}
        />
        <p className="text-xs text-stone-500 mt-3">Upload EIN correspondence, banker instructions, or signature-card packets.</p>
      </div>
    </div>
  );
}
